/**
 * A prepared Blob may be large enough to matter on a phone. Keep it only
 * long enough for the person to notice the fresh-tap prompt and act on it.
 */
export const PREPARED_BLOB_TTL_MS = 2 * 60 * 1000;

/** Do not click a signed URL that is at, or moments from, its expiry. */
export const DOWNLOAD_URL_SAFETY_MARGIN_MS = 5 * 1000;

export interface PreparedDownloadUrl {
  url: string;
  preparedAtMs: number;
  validForMs: number;
}

export function parsePreparedDownloadUrl(
  value: unknown,
  preparedAtMs: number = performance.now(),
): PreparedDownloadUrl | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const entry = value as Record<string, unknown>;
  if (
    typeof entry.url !== 'string'
    || typeof entry.expiresAt !== 'string'
    || typeof entry.expiresInSeconds !== 'number'
  ) return null;
  const expiresAtMs = new Date(entry.expiresAt).getTime();
  const validForMs = entry.expiresInSeconds * 1000;
  if (
    !Number.isFinite(expiresAtMs)
    || !Number.isFinite(preparedAtMs)
    || !Number.isFinite(validForMs)
    || validForMs <= DOWNLOAD_URL_SAFETY_MARGIN_MS
  ) return null;
  try {
    const parsed = new URL(entry.url);
    if (
      parsed.protocol !== 'https:'
      || parsed.username !== ''
      || parsed.password !== ''
      || parsed.hash !== ''
    ) return null;
  } catch {
    return null;
  }
  return { url: entry.url, preparedAtMs, validForMs };
}

export function isPreparedDownloadUrlFresh(
  preparedAtMs: number,
  validForMs: number,
  nowMs: number = performance.now(),
): boolean {
  return Number.isFinite(preparedAtMs)
    && Number.isFinite(validForMs)
    && validForMs > DOWNLOAD_URL_SAFETY_MARGIN_MS
    && preparedAtMs + validForMs - DOWNLOAD_URL_SAFETY_MARGIN_MS > nowMs;
}

export type CurrentSaveDelivery<T> =
  | { status: 'stale' }
  | { status: 'delivered'; prepared: T };

/**
 * Await a save's network preparation, then cross the delivery boundary only
 * if the rendered item is still the item whose tap started the work.
 *
 * Keep this coordinator shared. A guard that lives after share()/click() can
 * suppress stale UI but cannot undo delivery of item A under item B's row.
 */
export async function prepareAndDeliverCurrentSave<T>(input: {
  attemptIdentity: string;
  currentIdentity: () => string;
  prepare: () => Promise<T>;
  deliver: (prepared: T) => Promise<void>;
}): Promise<CurrentSaveDelivery<T>> {
  const prepared = await input.prepare();
  if (input.currentIdentity() !== input.attemptIdentity) return { status: 'stale' };
  await input.deliver(prepared);
  return { status: 'delivered', prepared };
}
